import React, { useState, useEffect } from 'react';
import { Settings, RotateCcw, Save, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DIMENSION_LABELS = {
  title_match: { label: 'Title Match', desc: 'Points for role title similarity', fields: [
    { key: 'exact', label: 'Exact Match' },
    { key: 'near', label: 'Near Match (70%+)' },
    { key: 'partial', label: 'Partial Match (40%+)' },
    { key: 'related', label: 'Related Title' },
    { key: 'weak', label: 'Weak Overlap' },
  ]},
  function_match: { label: 'Function Match', desc: 'Bonus/penalty for job function alignment', fields: [
    { key: 'match', label: 'Function Match' },
    { key: 'mismatch', label: 'Function Mismatch (penalty)' },
  ]},
  seniority: { label: 'Seniority', desc: 'Points for seniority level alignment', fields: [
    { key: 'exact', label: 'Exact Level' },
    { key: 'close', label: 'Close (1 level)' },
    { key: 'far', label: 'Far Mismatch (penalty)' },
    { key: 'unknown', label: 'Unknown (penalty)' },
  ]},
  location: { label: 'Location', desc: 'Points for geographic match', fields: [
    { key: 'city', label: 'City Match' },
    { key: 'state', label: 'State Match' },
  ]},
  keywords: { label: 'Skills/Keywords', desc: 'Points for keyword overlap in title', fields: [
    { key: 'per_keyword', label: 'Per Keyword' },
    { key: 'max', label: 'Max Points' },
  ]},
  industry: { label: 'Industry Relevance', desc: 'Points for vertical/department match', fields: [
    { key: 'match', label: 'Industry Match' },
  ]},
};

export const ScoringConfig = ({ onClose, onSaved }) => {
  const [weights, setWeights] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedDim, setExpandedDim] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('backoffice_token');
      const res = await fetch(`${API_URL}/api/intelligence/acquisition/scoring-config`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setWeights(data.weights);
      setDefaults(data.defaults);
    } catch { toast.error('Failed to load scoring config'); }
    setLoading(false);
  };

  const updateField = (dim, field, value) => {
    const num = parseInt(value, 10);
    if (isNaN(num)) return;
    setWeights(prev => ({
      ...prev,
      [dim]: { ...prev[dim], [field]: num },
    }));
    setHasChanges(true);
  };

  const resetToDefaults = () => {
    if (defaults) {
      setWeights(JSON.parse(JSON.stringify(defaults)));
      setHasChanges(true);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('backoffice_token');
      const res = await fetch(`${API_URL}/api/intelligence/acquisition/scoring-config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ weights }),
      });
      if (res.ok) {
        toast.success('Scoring weights saved. Re-score roles to apply changes.');
        setHasChanges(false);
        if (onSaved) onSaved();
      }
    } catch { toast.error('Failed to save config'); }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!weights) return null;

  // Calculate total possible max
  const totalMax = (weights.title_match.exact + weights.function_match.match +
    weights.seniority.exact + weights.location.city + weights.keywords.max + weights.industry.match);

  return (
    <div className="space-y-3" data-testid="scoring-config-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Settings className="h-4 w-4 text-[#0A1F44]" />
          <h3 className="text-sm font-bold text-[#0A1F44]">Scoring Weights Configuration</h3>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[9px] text-gray-400">Max possible: {totalMax} pts</span>
          <button onClick={resetToDefaults} className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500" title="Reset to defaults"
            data-testid="scoring-config-reset">
            <RotateCcw className="h-3 w-3" />
          </button>
          {onClose && (
            <button onClick={onClose} className="p-1.5 rounded-md hover:bg-gray-100 text-gray-400 text-[10px] font-bold">
              Close
            </button>
          )}
        </div>
      </div>

      <p className="text-[10px] text-gray-500">
        Adjust how candidate scores are calculated. After saving, re-score candidates to see updated results.
      </p>

      <div className="space-y-1">
        {Object.entries(DIMENSION_LABELS).map(([dimKey, dimConfig]) => {
          const isOpen = expandedDim === dimKey;
          return (
            <div key={dimKey} className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setExpandedDim(isOpen ? null : dimKey)}
                className="w-full flex items-center justify-between p-2.5 hover:bg-gray-50 transition-colors"
              >
                <div>
                  <span className="text-[11px] font-semibold text-[#0A1F44]">{dimConfig.label}</span>
                  <p className="text-[9px] text-gray-400">{dimConfig.desc}</p>
                </div>
                {isOpen ? <ChevronUp className="h-3 w-3 text-gray-400" /> : <ChevronDown className="h-3 w-3 text-gray-400" />}
              </button>
              {isOpen && (
                <div className="px-2.5 pb-2.5 space-y-2 border-t border-gray-100">
                  {dimConfig.fields.map(f => (
                    <div key={f.key} className="flex items-center justify-between mt-2">
                      <label className="text-[10px] text-gray-600">{f.label}</label>
                      <div className="flex items-center gap-1.5">
                        <input
                          type="number"
                          value={weights[dimKey]?.[f.key] ?? 0}
                          onChange={e => updateField(dimKey, f.key, e.target.value)}
                          className="w-14 px-1.5 py-1 text-[10px] text-center font-mono border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#D4AF37]"
                          data-testid={`scoring-config-${dimKey}-${f.key}`}
                        />
                        <span className="text-[8px] text-gray-400">pts</span>
                        {defaults && weights[dimKey]?.[f.key] !== defaults[dimKey]?.[f.key] && (
                          <span className="text-[7px] text-amber-500 font-bold">Modified</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {hasChanges && (
        <div className="flex items-center justify-between pt-2 border-t border-gray-200">
          <span className="text-[9px] text-amber-600 font-medium">Unsaved changes</span>
          <button onClick={saveConfig} disabled={saving}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#0A1F44] text-white text-[10px] font-bold rounded-md hover:bg-[#0A1F44]/90 disabled:opacity-50"
            data-testid="scoring-config-save">
            {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
            Save Weights
          </button>
        </div>
      )}
    </div>
  );
};
